import React, { useCallback, useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { KeyRound, ShieldCheck, Smartphone, Mail, Copy, Laptop, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { CodeInput } from "@/components/AuthSteps";

const Card = ({ icon: Icon, title, right, children, testid }) => (
  <div className="bg-white border border-slate-200 rounded-md mt-6" data-testid={testid}>
    <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
      <Icon className="w-4 h-4" />
      <div className="text-sm font-semibold tracking-tight">{title}</div>
      <div className="ml-auto">{right}</div>
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const PwField = ({ id, label, value, onChange }) => (
  <div>
    <Label htmlFor={id} className="text-xs uppercase tracking-wider text-slate-500">{label}</Label>
    <Input id={id} data-testid={id} type="password" value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 mono" />
  </div>
);

export const ChangePasswordCard = ({ onChanged }) => {
  const [f, setF] = useState({ current: "", next: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (f.next.length < 6) return toast.error("New password must be at least 6 characters");
    if (f.next !== f.confirm) return toast.error("New passwords don't match");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: f.current, new_password: f.next });
      toast.success("Password changed. Other devices have been signed out.");
      setF({ current: "", next: "", confirm: "" });
      onChanged?.();
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card icon={KeyRound} title="Change password" testid="change-password-card">
      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <PwField id="cp-current" label="Current password" value={f.current} onChange={set("current")} />
        <PwField id="cp-new" label="New password" value={f.next} onChange={set("next")} />
        <PwField id="cp-confirm" label="Confirm new password" value={f.confirm} onChange={set("confirm")} />
        <div className="md:col-span-3 flex flex-wrap items-center gap-3">
          <Button data-testid="cp-submit" type="submit" disabled={busy || !f.current || !f.next} className="bg-blue-700 hover:bg-blue-800 text-white">
            {busy ? "Saving…" : "Update password"}
          </Button>
          <p className="text-xs text-slate-500">You'll stay signed in here; all other devices will be signed out. Signed up with Google? Use "Forgot password" on the sign-in page to set one.</p>
        </div>
      </form>
    </Card>
  );
};

const BackupCodes = ({ codes, onClose }) => (
  <div className="border border-amber-200 bg-amber-50 rounded-md p-4" data-testid="backup-codes-panel">
    <div className="text-sm font-semibold">Save your backup codes</div>
    <p className="text-xs text-slate-600 mt-1">Each code works once if you lose access to your app or email. They won't be shown again.</p>
    <div className="grid grid-cols-2 gap-2 mt-3 mono text-sm" data-testid="backup-codes-list">
      {codes.map((c) => <div key={c} className="bg-white border border-amber-200 rounded px-2 py-1 text-center">{c}</div>)}
    </div>
    <div className="flex gap-2 mt-3">
      <Button size="sm" variant="outline" data-testid="backup-codes-copy" onClick={() => { navigator.clipboard?.writeText(codes.join("\n")); toast.success("Copied"); }} className="gap-1"><Copy className="w-3 h-3" />Copy</Button>
      <Button size="sm" data-testid="backup-codes-done" onClick={onClose} className="bg-blue-700 hover:bg-blue-800 text-white">I've saved them</Button>
    </div>
  </div>
);

const MethodButton = ({ icon: Icon, title, text, onClick, testid, disabled }) => (
  <button type="button" data-testid={testid} onClick={onClick} disabled={disabled}
    className="text-left border border-slate-200 rounded-md p-4 hover:border-blue-600 transition-colors disabled:opacity-50">
    <Icon className="w-5 h-5 mb-2" />
    <div className="text-sm font-semibold">{title}</div>
    <div className="text-xs text-slate-500 mt-1">{text}</div>
  </button>
);

const SetupFlow = ({ setup, onEnabled, onCancel }) => {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const enable = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/enable", { code });
      onEnabled(data.backup_codes);
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={enable} className="space-y-4" data-testid="two-factor-setup-flow">
      {setup.method === "totp" ? (
        <div className="flex flex-col md:flex-row gap-5 items-start">
          <img src={setup.qr_data_url} alt="Authenticator QR code" data-testid="two-factor-qr" className="w-40 h-40 border border-slate-200 rounded" />
          <div className="text-sm text-slate-600 space-y-2">
            <p>1. Scan this QR code with Google Authenticator, Microsoft Authenticator or a similar app.</p>
            <p>Can't scan? Enter this key manually:</p>
            <div className="mono text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1 break-all" data-testid="two-factor-secret">{setup.secret}</div>
            <p>2. Enter the 6-digit code shown in the app.</p>
          </div>
        </div>
      ) : (
        <p className="text-sm text-slate-600">We sent a 6-digit code to your email. Enter it to confirm.</p>
      )}
      <CodeInput value={code} onChange={setCode} testid="two-factor-setup-code" />
      <div className="flex gap-2">
        <Button type="submit" data-testid="two-factor-enable" disabled={busy || code.length !== 6} className="bg-blue-700 hover:bg-blue-800 text-white">{busy ? "Checking…" : "Turn on"}</Button>
        <Button type="button" variant="outline" data-testid="two-factor-setup-cancel" onClick={onCancel}>Cancel</Button>
      </div>
    </form>
  );
};

const ManageEnabled = ({ status, onChanged, onCodes }) => {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async (url, after) => {
    setBusy(true);
    try {
      const { data } = await api.post(url, { code });
      setCode("");
      after(data);
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };
  const sendEmail = async () => {
    try { await api.post("/auth/2fa/email-code"); toast.success("Code sent to your email"); } catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-600">
        Signing in asks for a code from your {status.method === "totp" ? "authenticator app" : "email"}. Backup codes left: <b data-testid="backup-codes-left">{status.backup_codes_left}</b>
      </p>
      <p className="text-xs text-slate-500">To turn off or get new backup codes, enter a current code{status.method === "email" ? " (we'll email one)" : ""} or a backup code.</p>
      <div className="flex flex-wrap items-center gap-2">
        <Input data-testid="two-factor-manage-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Code" className="w-40 mono" />
        {status.method === "email" && <Button variant="outline" data-testid="two-factor-send-email-code" onClick={sendEmail}>Email me a code</Button>}
        <Button variant="outline" data-testid="two-factor-regen-codes" disabled={busy || code.length < 6}
          onClick={() => run("/auth/2fa/backup-codes", (d) => onCodes(d.backup_codes))}>New backup codes</Button>
        <Button variant="destructive" data-testid="two-factor-disable" disabled={busy || code.length < 6}
          onClick={() => run("/auth/2fa/disable", () => { toast.success("Two-step sign-in turned off"); onChanged(); })}>Turn off</Button>
      </div>
    </div>
  );
};

export const TwoFactorCard = () => {
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState(null);
  const [setup, setSetup] = useState(null);
  const [codes, setCodes] = useState(null);
  const load = useCallback(async () => {
    try { setStatus((await api.get("/auth/2fa/status")).data); } catch (e) { toast.error(formatErr(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const changed = () => { load(); refreshUser(); };
  const start = async (method) => {
    try { setSetup((await api.post("/auth/2fa/setup", { method })).data); } catch (e) { toast.error(formatErr(e)); }
  };
  const badge = status?.enabled
    ? <Badge data-testid="two-factor-status" className="bg-emerald-600 hover:bg-emerald-600">On</Badge>
    : <Badge data-testid="two-factor-status" variant="outline">Off</Badge>;
  let body = null;
  if (codes) body = <BackupCodes codes={codes} onClose={() => setCodes(null)} />;
  else if (setup) body = <SetupFlow setup={setup} onCancel={() => setSetup(null)} onEnabled={(c) => { setSetup(null); setCodes(c); toast.success("Two-step sign-in is on"); changed(); }} />;
  else if (status?.enabled) body = <ManageEnabled status={status} onChanged={changed} onCodes={(c) => { setCodes(c); load(); }} />;
  else if (status) body = (
    <div>
      <p className="text-sm text-slate-600 mb-4">Add a second step after your password. Choose how you'd like to receive codes:</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <MethodButton icon={Smartphone} testid="two-factor-choose-totp" title="Authenticator app" text="Codes from Google/Microsoft Authenticator. Works offline." onClick={() => start("totp")} />
        <MethodButton icon={Mail} testid="two-factor-choose-email" title="Email code" text="We email you a 6-digit code each time you sign in." onClick={() => start("email")} />
      </div>
    </div>
  );
  return <Card icon={ShieldCheck} title="Two-step sign-in" right={status && badge} testid="two-factor-card">{body}</Card>;
};

const fmt = (iso) => new Date(iso).toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

const DeviceRow = ({ d, onRemove }) => (
  <div className="flex items-center gap-3 py-3 border-b border-slate-100 last:border-0" data-testid={`trusted-device-${d.id}`}>
    <Laptop className="w-4 h-4 text-slate-500" />
    <div className="flex-1 min-w-0">
      <div className="text-sm font-medium flex items-center gap-2">{d.label}
        {d.current && <Badge variant="outline" data-testid="trusted-device-current" className="text-emerald-700 border-emerald-300">This device</Badge>}
      </div>
      <div className="text-xs text-slate-500">Added {fmt(d.created_at)} · Last used {fmt(d.last_used_at)} · Expires {fmt(d.expires_at)}</div>
    </div>
    <Button size="sm" variant="outline" data-testid={`trusted-device-remove-${d.id}`} onClick={() => onRemove(d.id)} className="gap-1"><Trash2 className="w-3 h-3" />Remove</Button>
  </div>
);

export const TrustedDevicesCard = () => {
  const [devices, setDevices] = useState(null);
  const load = useCallback(async () => {
    try { setDevices((await api.get("/auth/trusted-devices")).data); } catch (e) { toast.error(formatErr(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const remove = async (url, msg) => {
    try { await api.delete(url); toast.success(msg); load(); } catch (e) { toast.error(formatErr(e)); }
  };
  const right = devices?.length > 0 && (
    <Button size="sm" variant="outline" data-testid="trusted-devices-remove-all" onClick={() => remove("/auth/trusted-devices", "All trusted devices removed")}>Remove all</Button>
  );
  return (
    <Card icon={Laptop} title="Trusted devices" right={right} testid="trusted-devices-card">
      <p className="text-xs text-slate-500 mb-2">These devices skip the 2-step code for 30 days. They're cleared automatically when you change your password or turn off two-step sign-in.</p>
      {devices && devices.length === 0 && <p className="text-sm text-slate-500" data-testid="trusted-devices-empty">No trusted devices yet.</p>}
      {devices?.map((d) => <DeviceRow key={d.id} d={d} onRemove={(id) => remove(`/auth/trusted-devices/${id}`, "Device removed")} />)}
    </Card>
  );
};

export const AccountSecurity = () => {
  const { user } = useAuth();
  const [ver, setVer] = useState(0);
  return (
    <section data-testid="account-security-section" className="mt-10">
      <div className="text-[11px] uppercase tracking-wider text-slate-500">Account security</div>
      <ChangePasswordCard onChanged={() => setVer((v) => v + 1)} />
      <TwoFactorCard />
      {user?.two_factor_method && <TrustedDevicesCard key={`${user.two_factor_method}-${ver}`} />}
    </section>
  );
};
